import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { HexColorPicker, RgbaColorPicker } from 'react-colorful';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  label?: string;
}

export const ColorPicker = ({ color, onChange, label }: ColorPickerProps) => {
  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'solid' | 'gradient'>('solid');
  const [gradientType, setGradientType] = useState<'linear' | 'radial'>('linear');
  const [gradientAngle, setGradientAngle] = useState(90);
  const [gradientStop1, setGradientStop1] = useState('#ffffff');
  const [gradientStop2, setGradientStop2] = useState('#000000');
  
  const handleSolidColorChange = (newColor: string) => {
    onChange(newColor);
  };
  
  const handleGradientChange = () => {
    if (gradientType === 'linear') {
      onChange(`linear-gradient(${gradientAngle}deg, ${gradientStop1}, ${gradientStop2})`);
    } else {
      onChange(`radial-gradient(circle, ${gradientStop1}, ${gradientStop2})`);
    }
  };
  
  const updateGradientStop1 = (newColor: string) => {
    setGradientStop1(newColor);
    handleGradientChange();
  };
  
  const updateGradientStop2 = (newColor: string) => {
    setGradientStop2(newColor);
    handleGradientChange();
  };
  
  const updateGradientAngle = (value: number[]) => {
    setGradientAngle(value[0]);
    handleGradientChange();
  };
  
  const updateGradientType = (value: 'linear' | 'radial') => {
    setGradientType(value);
    handleGradientChange();
  };
  
  return (
    <div className="flex flex-col gap-1.5">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-start gap-2"
            style={{ 
              background: color.startsWith('linear-gradient') || color.startsWith('radial-gradient') 
                ? color 
                : undefined 
            }}
          >
            <div 
              className="w-5 h-5 rounded ring-1 ring-border" 
              style={{ background: color }}
            />
            <span className="text-xs">{color}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3">
          <Tabs defaultValue="solid" value={selectedTab} onValueChange={(value) => setSelectedTab(value as 'solid' | 'gradient')}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="solid">Solid</TabsTrigger>
              <TabsTrigger value="gradient">Gradient</TabsTrigger>
            </TabsList>
            
            <TabsContent value="solid" className="mt-2">
              <div className="flex flex-col gap-2">
                <HexColorPicker color={color} onChange={handleSolidColorChange} className="w-full" />
                <Input 
                  type="text" 
                  value={color} 
                  onChange={(e) => handleSolidColorChange(e.target.value)}
                  className="mt-2"
                />
              </div>
            </TabsContent>
            
            <TabsContent value="gradient" className="mt-2">
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant={gradientType === 'linear' ? 'default' : 'outline'} 
                    onClick={() => updateGradientType('linear')}
                    size="sm"
                  >
                    Linear
                  </Button>
                  <Button 
                    variant={gradientType === 'radial' ? 'default' : 'outline'} 
                    onClick={() => updateGradientType('radial')}
                    size="sm"
                  >
                    Radial
                  </Button>
                </div>
                
                {gradientType === 'linear' && (
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">Angle: {gradientAngle}°</Label>
                    <Slider
                      defaultValue={[gradientAngle]}
                      max={360}
                      step={1}
                      onValueChange={updateGradientAngle}
                    />
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">Color 1</Label>
                    <div 
                      className="w-full h-10 rounded cursor-pointer relative ring-1 ring-border" 
                      style={{ background: gradientStop1 }}
                    >
                      <HexColorPicker 
                        color={gradientStop1} 
                        onChange={updateGradientStop1} 
                        className="absolute top-full mt-1 z-10 left-0"
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">Color 2</Label>
                    <div 
                      className="w-full h-10 rounded cursor-pointer relative ring-1 ring-border" 
                      style={{ background: gradientStop2 }}
                    >
                      <HexColorPicker 
                        color={gradientStop2} 
                        onChange={updateGradientStop2} 
                        className="absolute top-full mt-1 z-10 right-0"
                      />
                    </div>
                  </div>
                </div>
                
                <div 
                  className="w-full h-10 rounded mt-2 ring-1 ring-border" 
                  style={{ 
                    background: gradientType === 'linear' 
                      ? `linear-gradient(${gradientAngle}deg, ${gradientStop1}, ${gradientStop2})`
                      : `radial-gradient(circle, ${gradientStop1}, ${gradientStop2})`
                  }}
                />
              </div>
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Helper function to determine if a color is dark (for text contrast)
function isColorDark(hexColor: string): boolean {
  // Remove the hash if it exists
  hexColor = hexColor.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hexColor.substr(0, 2), 16);
  const g = parseInt(hexColor.substr(2, 2), 16);
  const b = parseInt(hexColor.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return true if the color is dark
  return luminance < 0.5;
} 